import { Button, Card, CardContent, Grid, Tab, Tabs } from "@mui/material";
import React, { useCallback, useEffect, useState } from "react";
import DtTab from "./DtTab";
import LogTabs from "./LogTabs";
import { IRequestId } from "../../models/IRequestIds";
import PreviewDocsService from "../../services/previewDocsService";
import { REQUEST_TYPES } from "../../utils/constants";
import { useSelector } from "react-redux";
import { RootState } from "../../state/store";
import { useSolexEditor } from "../../hooks/useSolexEditor";
import { useSnackbar } from "../../hooks/useSnackbar";


interface OrderTabsProps {
    logsTabVisible: boolean,
    logsTabEnabled: boolean,
    logsTabTitle: string,
    requestResponseTabVisible: boolean,
    requestResponseTabEnabled: boolean,
    requestResponseTabTitle: string,
    requests: Array<IRequestId>,
    authToken: string,
    requestType: string,
}


const OrderTabs: React.FC<OrderTabsProps> = (
    {
        logsTabVisible,
        logsTabTitle,
        logsTabEnabled,
        requestResponseTabVisible,
        requestResponseTabTitle,
        requestResponseTabEnabled,
        requests,
        authToken,
        requestType
    }
) => {
    const previewDocsService = new PreviewDocsService();

    // Solex integration (EN-3255)
    const solexEditorLink = useSelector((state: RootState) => state.appSlice.orderState.solexEditorLink);

    // Toast notifications for OrderTabs
    const { showSnackbar, snackbar } = useSnackbar();

    // Solex editor hook with toast handler (handles both feature flag and config flag checks)
    const solexEditor = useSolexEditor(showSnackbar);
    const resolveTabValue = useCallback(() => {
        if (requestResponseTabVisible && requestResponseTabEnabled) return 0;
        if (logsTabVisible && logsTabEnabled) return 1;
        if (requestResponseTabVisible) return 0;
        if (logsTabVisible) return 1;
        return 0;
    }, [
        requestResponseTabVisible,
        requestResponseTabEnabled,
        logsTabVisible,
        logsTabEnabled
    ]);
    const [tabValue, setTabValue] = useState(resolveTabValue);
    const [hasOpenedLogsTab, setHasOpenedLogsTab] = useState(false);

    useEffect(() => {
        const resolvedTabValue = resolveTabValue();
        setTabValue(resolvedTabValue);
        if (resolvedTabValue === 1 && logsTabVisible && logsTabEnabled) {
            setHasOpenedLogsTab(true);
        }
    }, [resolveTabValue, logsTabVisible, logsTabEnabled]);

    useEffect(() => {
        if (!logsTabVisible || !logsTabEnabled) {
            setHasOpenedLogsTab(false);
        }
    }, [logsTabVisible, logsTabEnabled]);

    const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
        const isLogsTab = newValue === 1;
        if ((newValue === 0 && requestResponseTabEnabled) || (isLogsTab && logsTabEnabled)) {
            if (isLogsTab) {
                setHasOpenedLogsTab(true);
            }
            setTabValue(newValue);
        }
    }

    const handleLogsTabClick = () => {
        if (logsTabEnabled) {
            setHasOpenedLogsTab(true);
        }
    };

    const handleDownloadMetaData = async (transactionId: string | null) => {
        await previewDocsService.downloadMetadata(transactionId, authToken, requests);
    }

    const handleDownloadFiles = async (transactionId: string | null) => {
       await previewDocsService.previewDocument(transactionId,authToken,requests)
    }

    const isTabAreaVisible = requestResponseTabVisible || logsTabVisible;

    if (!isTabAreaVisible) {
        return null;
    }

    return (
        <Card sx={{ display: "block" }}>
            <CardContent sx={{ padding: 0 }}>
                <Tabs value={tabValue} onChange={handleTabChange}>
                    {requestResponseTabVisible && (
                        <Tab value={0} label={requestResponseTabTitle} disabled={!requestResponseTabEnabled} />
                    )}
                    {logsTabVisible && (
                        <Tab value={1} label={logsTabTitle} disabled={!logsTabEnabled} onClick={handleLogsTabClick} />
                    )}
                </Tabs>
                <DtTab value={tabValue} index={0}>
                    {requestResponseTabVisible && (
                        <>
                            {requests[0]?.RequestId &&
                                <Grid container justifyContent='flex-start'>
                                    Request Id: {requests[0]?.RequestId}
                                </Grid>
                            }
                            <Grid container justifyContent='flex-end'>
                                {/* Solex Editor Button - respects both LaunchDarkly and config flags (EN-3255) */}
                                {solexEditor.isEnabled && solexEditorLink && (
                                    <Button
                                        variant='outlined'
                                        size='small'
                                        color='primary'
                                        sx={{ marginRight: 1 }}
                                        onClick={async () => {
                                            await solexEditor.openManually(solexEditorLink);
                                        }}
                                        disabled={solexEditor.isOpeningEditor}
                                    >
                                        {solexEditor.isOpeningEditor ? 'Opening...' : 'Open Solex Doc Editor session'}
                                    </Button>
                                )}

                                <Button disabled={requests.length <= 0}
                                    sx={{ 'marginRight': 1 }}
                                    variant='outlined'
                                    size='small'
                                    color='info'
                                    onClick={async () => await handleDownloadMetaData(null)}>
                                    Download Request and Response
                                </Button>
                                <Button disabled={requests.length <= 0 || requestType === REQUEST_TYPES.LIGHTS_ON}
                                    variant="outlined"
                                    size="small"
                                    color="info"
                                    onClick={async () => await handleDownloadFiles(null)}>
                                    Preview Documents
                                </Button>
                            </Grid>
                        </>
                    )}
                </DtTab>
                <DtTab value={tabValue} index={1}>
                    {logsTabVisible && logsTabEnabled && hasOpenedLogsTab && <LogTabs />}
                </DtTab>
            </CardContent>
            {/* Toast notification support (EN-3255) */}
            {snackbar}
        </Card>
    )
}
export default OrderTabs; 